import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import FormInput from '@/components/common/form-input';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useUpdateNickName from '@/hooks/users/use-update-nickname';
import useUserInfo from '@/hooks/users/use-user-info';

export default function MyNicknameRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    resetField,
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<UpdateNicknameFormData>({
    resolver: zodResolver(updateNicknameFormScheme),
    defaultValues: { nickname: '' },
  });

  const { data: userInfo } = useUserInfo();
  const { mutateAsync: updateNickname, isPending } = useUpdateNickName();

  useAdjustLayout();

  const handleUpdateNickname = async (data: UpdateNicknameFormData) => {
    await updateNickname(data.nickname);

    navigate(-1);
  };

  useEffect(() => {
    if (userInfo?.NickName) {
      resetField('nickname', { defaultValue: userInfo.NickName });
    }
  }, [userInfo?.NickName]);

  return (
    <PageLayout
      title={t('personal-info.edit-nickname')}
      headerConfig={{
        navigateBack: true,
      }}
      blockOnDesktop>
      <form
        className='mx-5 mb-[1.3125rem] flex flex-1 flex-col justify-between'
        onSubmit={handleSubmit(handleUpdateNickname)}>
        <FormInput
          control={control}
          name='nickname'
        />

        <ProgressButton
          as='submit'
          disabled={!isDirty}
          isLoading={isPending}
          className='btn btn-primary'
          label='common.confirm'
        />
      </form>
    </PageLayout>
  );
}

const updateNicknameFormScheme = z.object({
  nickname: z.string().trim().min(1, 'errors.string.length|count=1').max(20, 'errors.string.max-length|count=20'),
});

type UpdateNicknameFormData = z.infer<typeof updateNicknameFormScheme>;
